import React, { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Checkin, Feature, ManualOverrideType } from '../api/client'
import PersonDetailModal, { PersonDetailValue } from './checkin/PersonDetailModal'
import EditCollectionItemById from './collections/EditCollectionItemById'
import PersonAvatar from './PersonAvatar'
import FeatureLockBadge from './FeatureLockBadge'
import { PersonLikeData } from '../utils/people'
import { checkinFeatureOwner } from '../utils/checkinFeatures'
import { resolveCollectionItemName } from '../utils/collectionUtils'

interface PersonChipsProps {
  checkin: Checkin
  collectionItems: Record<string, any>
  /** Page-level feature list (the user's active features). Used to resolve the
   *  user-global People feature_id without depending on the per-checkin
   *  `feature_details` — that field is built at fetchData time and goes stale
   *  when a parse adds People rows to a checkin loaded earlier. The composer
   *  uses the same global list for its chip lookup, so checkin chips and
   *  composer chips stay in sync. */
  features?: Feature[]
  /** Refetch trigger so the page can pick up edits made via the in-modal
   *  Edit hand-off. */
  onRefresh?: () => void
}

interface RenderedPerson {
  key: string
  label: string
  data: PersonLikeData
  detailValue: PersonDetailValue
}

const PEOPLE_FEATURE_TITLE = 'People'

// Cap chip labels so `whitespace-nowrap` doesn't let a long name overflow.
const MAX_LABEL_LEN = 30
const truncateLabel = (s: string): string =>
  s.length > MAX_LABEL_LEN ? `${s.slice(0, MAX_LABEL_LEN - 1)}…` : s

const findPeopleFeatureId = (
  checkin: Checkin,
  features?: Feature[],
): number | null => {
  // Prefer the global list (always reflects the user's bootstrapped People
  // feature once the page has fetched it) over `checkin.feature_details`,
  // which is reconstructed at fetchData time and can lag behind a recent
  // parse that attached the first-ever People row.
  const fromGlobal = (features || []).find(
    (f) => !f.project_id && f.title === PEOPLE_FEATURE_TITLE,
  )
  if (fromGlobal) return fromGlobal.id
  const fromCheckin = (checkin.feature_details || []).find(
    (f) => !f.project_id && f.title === PEOPLE_FEATURE_TITLE,
  )
  return fromCheckin ? fromCheckin.id : null
}

/**
 * Returns true when the checkin has at least one person worth rendering — i.e.
 * an active row of the user-global People feature with a `reference_value`
 * pointing at a known collection item. Mirrors `hasLocationChips`.
 *
 * Used by the checkin renderer to decide whether to spend a row on chips.
 */
export function hasPersonChips(
  checkin: Checkin,
  collectionItems: Record<string, any>,
  features?: Feature[],
): boolean {
  const peopleFeatureId = findPeopleFeatureId(checkin, features)
  if (peopleFeatureId == null) return false
  return checkin.checkin_features.some(
    (cf) =>
      cf.feature_id === peopleFeatureId &&
      cf.manual_override !== ManualOverrideType.DELETE &&
      cf.reference_value != null &&
      collectionItems[String(cf.reference_value)] != null,
  )
}

/**
 * Read-only chip strip of People attached to a checkin. Tapping a chip opens
 * `PersonDetailModal`. No edit affordance — this is a viewer surface (per the
 * `feedback_review_vs_edit` memory). Edits happen on `PeopleCollectionPage`.
 *
 * Source: the user-global People feature (`project_id IS NULL`,
 * `title='People'`). Resolution prefers the page-level `features` list (kept
 * fresh by `mergeFeatures` after a parse bootstraps the People feature),
 * falling back to `checkin.feature_details` for compatibility — see
 * `findPeopleFeatureId` for the rationale.
 */
const PersonChips: React.FC<PersonChipsProps> = ({ checkin, collectionItems, features, onRefresh }) => {
  const [openValue, setOpenValue] = useState<PersonDetailValue | null>(null)
  const [editingCollectionItem, setEditingCollectionItem] = useState<
    { collectionId: number; itemId: number } | null
  >(null)

  const peopleFeatureId = findPeopleFeatureId(checkin, features)
  if (peopleFeatureId == null) return null

  const activeRows = checkin.checkin_features.filter(
    (cf) =>
      cf.feature_id === peopleFeatureId &&
      cf.manual_override !== ManualOverrideType.DELETE,
  )

  const seen = new Set<number>()
  const rendered: RenderedPerson[] = []
  for (const row of activeRows) {
    if (row.reference_value == null) continue
    const id = row.reference_value
    if (seen.has(id)) continue
    const item = collectionItems[String(id)]
    const data: PersonLikeData = item?.data || {}
    // Render the chip via the People collection's `display_template` (today
    // `{first name}`) so chips read identically to refs rendered elsewhere
    // (map keys, reference-list chips, the composer's own chip-strip). Skip
    // rows that can't be resolved — this includes the brief window between
    // a parse attaching the row and the collection item cache hydrating.
    const label = resolveCollectionItemName(id, collectionItems)
    if (!label) continue
    seen.add(id)
    rendered.push({
      key: `person-${id}`,
      label,
      data,
      detailValue: { collectionItemId: id, data },
    })
  }

  if (rendered.length === 0) return null

  // The lock is a property of the feature on this checkin, not of one person,
  // so the padlock is drawn once beside the strip. See
  // `checkinFeatureOwner` and `docs/list_tagging.md`.
  const owner = checkinFeatureOwner(checkin, peopleFeatureId)

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <AnimatePresence initial={true}>
          {rendered.map((person) => (
            <motion.button
              key={person.key}
              type="button"
              style={{ transformOrigin: 'left center' }}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setOpenValue(person.detailValue)}
              className="inline-flex items-center rounded-full text-xs font-medium bg-sky-100 text-sky-900 pl-1 pr-3 py-0.5 hover:bg-sky-200 transition-colors cursor-pointer whitespace-nowrap"
              title={`View ${person.label}`}
            >
              <PersonAvatar
                data={person.data}
                className="h-5 w-5 mr-1.5"
                initialsClassName="text-[9px]"
                alt={person.label}
              />
              {truncateLabel(person.label)}
            </motion.button>
          ))}
        </AnimatePresence>
        {owner !== 'parser' && (
          <FeatureLockBadge
            className="ml-0"
            owner={owner}
            title="Manually set — a re-parse leaves these people alone"
          />
        )}
      </div>

      <PersonDetailModal
        open={openValue !== null}
        value={openValue}
        onEdit={(() => {
          const id = openValue?.collectionItemId
          if (id == null) return undefined
          const item = collectionItems[String(id)]
          const collectionId = item?.collection_id
          if (collectionId == null) return undefined
          return () => {
            setOpenValue(null)
            setEditingCollectionItem({ collectionId, itemId: id })
          }
        })()}
        onClose={() => setOpenValue(null)}
      />
      {editingCollectionItem && (
        <EditCollectionItemById
          collectionId={editingCollectionItem.collectionId}
          itemId={editingCollectionItem.itemId}
          onClose={() => setEditingCollectionItem(null)}
          onSuccess={() => {
            setEditingCollectionItem(null)
            onRefresh?.()
          }}
        />
      )}
    </>
  )
}

export default PersonChips
